import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import client, { mensajeDeError } from '../api/client';
import { useAuth } from '../context/AuthContext';
import WeekView from '../components/WeekView';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import { Selector, MensajeError, MensajeExito } from '../components/ui/Form';
import { Contenedor, TituloPagina, Subtitulo } from '../components/layout/Layout';
import { lunesDeSemana, sumarDias, aISO, fechaLegible, fechaCompleta, horaLegible, MESES } from '../utils/fechas';

const Controles = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.sm};
  align-items: center;
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

const Semana = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  margin-right: auto;

  strong {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: 1.05rem;
    color: ${({ theme }) => theme.colors.sageDeep};
    min-width: 170px;
    text-align: center;
  }
`;

const Flecha = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.sageDeep};
  font-size: 1.1rem;
  cursor: pointer;
  transition: background 200ms ease;

  &:hover {
    background: ${({ theme }) => theme.colors.sageLight};
  }
`;

const DetalleReserva = styled.div`
  background: ${({ theme }) => theme.colors.sand};
  border-radius: ${({ theme }) => theme.radii.sm};
  padding: ${({ theme }) => theme.space.md};
  margin-bottom: ${({ theme }) => theme.space.md};

  p {
    font-size: 0.95rem;
  }

  strong {
    color: ${({ theme }) => theme.colors.sageDeep};
  }
`;

const Acciones = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};
  justify-content: flex-end;
`;

function rangoLegible(lunes) {
  const domingo = sumarDias(lunes, 6);
  return `${lunes.getDate()} — ${domingo.getDate()} de ${MESES[domingo.getMonth()]}`;
}

function Agenda() {
  const { usuario } = useAuth();
  const navigate = useNavigate();

  const [lunes, setLunes] = useState(() => lunesDeSemana());
  const [clases, setClases] = useState(null);
  const [tipos, setTipos] = useState([]);
  const [instructores, setInstructores] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroInstructor, setFiltroInstructor] = useState('');

  const [seleccionada, setSeleccionada] = useState(null);
  const [reservando, setReservando] = useState(false);
  const [error, setError] = useState('');
  const [exito, setExito] = useState('');

  useEffect(() => {
    client.get('/tipos-clase').then(({ data }) => setTipos(data)).catch(() => {});
    client.get('/instructores').then(({ data }) => setInstructores(data)).catch(() => {});
  }, []);

  const cargarClases = useCallback(() => {
    const params = {
      desde: aISO(lunes),
      hasta: aISO(sumarDias(lunes, 6)),
      soloFuturas: '1', // las clases que ya pasaron no se muestran
    };
    if (filtroTipo) params.tipoClaseId = filtroTipo;
    if (filtroInstructor) params.instructorId = filtroInstructor;
    client
      .get('/clases', { params })
      .then(({ data }) => setClases(data))
      .catch(() => setClases([]));
  }, [lunes, filtroTipo, filtroInstructor]);

  useEffect(() => {
    setClases(null);
    cargarClases();
  }, [cargarClases]);

  const alReservar = (clase) => {
    if (!usuario) {
      navigate('/login', { state: { desde: '/agenda' } });
      return;
    }
    setError('');
    setExito('');
    setSeleccionada(clase);
  };

  const confirmarReserva = async () => {
    setReservando(true);
    setError('');
    try {
      await client.post('/reservas', { claseId: seleccionada.id });
      setExito(
        `Reserva confirmada: ${seleccionada.tipo_clase} el ${fechaLegible(seleccionada.fecha)} a las ${horaLegible(seleccionada.hora_inicio)}.`
      );
      setSeleccionada(null);
      cargarClases();
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setReservando(false);
    }
  };

  return (
    <Contenedor>
      <TituloPagina>Agenda semanal</TituloPagina>
      <Subtitulo>
        Elige tu clase de la semana. Recuerda que puedes cancelar sin costo hasta 12 horas antes.
      </Subtitulo>

      <Controles>
        <Semana>
          <Flecha onClick={() => setLunes((l) => sumarDias(l, -7))} aria-label="Semana anterior">
            ‹
          </Flecha>
          <strong>{rangoLegible(lunes)}</strong>
          <Flecha onClick={() => setLunes((l) => sumarDias(l, 7))} aria-label="Semana siguiente">
            ›
          </Flecha>
        </Semana>
        <Selector
          value={filtroTipo}
          onChange={(e) => setFiltroTipo(e.target.value)}
          aria-label="Filtrar por tipo de clase"
        >
          <option value="">Todas las clases</option>
          {tipos.map((t) => (
            <option key={t.id} value={t.id}>
              {t.nombre}
            </option>
          ))}
        </Selector>
        <Selector
          value={filtroInstructor}
          onChange={(e) => setFiltroInstructor(e.target.value)}
          aria-label="Filtrar por instructor"
        >
          <option value="">Todos los instructores</option>
          {instructores.map((i) => (
            <option key={i.id} value={i.id}>
              {i.nombre}
            </option>
          ))}
        </Selector>
      </Controles>

      {exito && <MensajeExito role="status">{exito}</MensajeExito>}
      {error && !seleccionada && <MensajeError role="alert">{error}</MensajeError>}

      {clases === null ? (
        <Spinner />
      ) : (
        <WeekView clases={clases} lunes={lunes} onReservar={alReservar} />
      )}

      <Modal
        abierto={!!seleccionada}
        titulo="Confirmar reserva"
        onCerrar={() => setSeleccionada(null)}
      >
        {seleccionada && (
          <>
            <DetalleReserva>
              <p>
                <strong>{seleccionada.tipo_clase}</strong> con {seleccionada.instructor}
              </p>
              <p>
                {fechaCompleta(seleccionada.fecha)} · {horaLegible(seleccionada.hora_inicio)} ·{' '}
                {seleccionada.duracion_minutos} min
              </p>
            </DetalleReserva>
            <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
              Se descontará una clase de tu paquete activo. Puedes cancelar sin costo hasta 12
              horas antes de la clase.
            </p>
            {error && <MensajeError role="alert">{error}</MensajeError>}
            <Acciones>
              <Button $variante="fantasma" onClick={() => setSeleccionada(null)}>
                Volver
              </Button>
              <Button onClick={confirmarReserva} disabled={reservando}>
                {reservando ? 'Reservando…' : 'Confirmar reserva'}
              </Button>
            </Acciones>
          </>
        )}
      </Modal>
    </Contenedor>
  );
}

export default Agenda;
